import dayjs from 'dayjs'
import 'dayjs/locale/fr' // Import de la locale française
import relativeTime from 'dayjs/plugin/relativeTime'

// Activation du plugin et réglage de la langue
dayjs.extend(relativeTime)
dayjs.locale('fr')

/**
 * @description Get current date in format YYYY-MM-DD
 * @returns current date
 */
export function getCurrentDateFormated() {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * @description Parse seconds into hours, minutes and seconds
 * @param recievedSeconds
 * @returns [hours, minutes, seconds]
 */
export function parseTime(recievedSeconds: number): [number, number, number] {
  const hours = Math.floor(recievedSeconds / 3600)
  const minutes = Math.floor((recievedSeconds - hours * 3600) / 60)
  const seconds = recievedSeconds - hours * 3600 - minutes * 60
  return [hours, minutes, seconds]
}

/**
 * Format seconds into human readable time
 * @param seconds
 * @returns formatted time
 */
export function formatHumanTime(seconds: number) {
  const [h, m, s] = parseTime(seconds)
  return `${h} hour${h === 1 ? '' : 's'}, ${m} minute${m === 1 ? '' : 's'
    }, ${s} second${s === 1 ? '' : 's'}`
}

/**
 * @description Check if date1 is before date2
 * @param date1 - First date to compare
 * @param date2 - Second date to compare
 * @returns {boolean} True if date1 is before date2, false otherwise
 * @example
 * const date1 = new Date('2024-01-01');
 * const date2 = new Date('2024-02-01');
 * const result = isBefore(date1, date2); // returns true
 */
export function isBefore(date1: Date, date2: Date) {
  return dayjs(date1).isBefore(dayjs(date2))
}

/**
 * @description Check if date1 is after date2
 * @param date1 - First date to compare
 * @param date2 - Second date to compare
 * @returns {boolean} True if date1 is after date2, false otherwise
 * @example
 * const date1 = new Date('2024-02-01');
 * const date2 = new Date('2024-01-01');
 * const result = isAfter(date1, date2); // returns true
 */
export function isAfter(date1: Date, date2: Date) {
  return dayjs(date1).isAfter(dayjs(date2))
}

/**
 * @description Check if two dates are the same day
 * @param date1 - First date to compare (can be Date or string)
 * @param date2 - Second date to compare (can be Date or string)
 * @returns {boolean} True if both dates are the same day, false otherwise
 * @example
 * const date1 = new Date('2024-01-01 14:30:00');
 * const date2 = new Date('2024-01-01 09:15:00');
 * const result = isSameDay(date1, date2); // returns true
 */
export function isSameDay(date1: Date | string, date2: Date | string) {
  return dayjs(date1).isSame(dayjs(date2), 'day')
}

/**
 * @description Get the time left between two dates
 * @param date1 - First date to compare
 * @param date2 - Second date to compare
 * @returns {number} The time left in seconds
 * @example
 * const date1 = new Date('2024-01-01 14:30:00');
 * const date2 = new Date('2024-01-01 09:15:00');
 * const result = getTimeLeft(date1, date2); // returns 18900
 */
export function getTimeLeft(date1: Date | string, date2: Date | string) {
  return dayjs(date1).diff(dayjs(date2), 'seconds')
}

/**
 * @description Check if a date is less than 2 days old
 * @param date - The date to check (can be Date or string)
 * @param days - The number of days to check (default is 2)
 * @returns {boolean} True if the date is less than 2 days old, false otherwise
 * @example
 * const date = new Date('2024-01-01');
 * const result = isLessThanTwoDays(date); // returns true if date is less than 2 days old
 */
export function isLessThanXDays({
  date,
  days = 2,
}: {
  date: Date | string
  days?: number
}): boolean {
  const now = dayjs()
  const targetDate = dayjs(date)
  const diffInDays = now.diff(targetDate, 'day')
  return diffInDays < days
}
